/**
 * MongoDB Key Sanitization Utility for Frontend
 *
 * This utility sanitizes keys to make them MongoDB-compatible by removing
 * invalid characters rather than rejecting them.
 */

const MAX_KEY_LENGTH = 512;

/**
 * Sanitizes a key to make it MongoDB-compatible
 * - Removes . (dot) characters
 * - Removes $ characters from the start
 * - Trims to maximum length
 * - Replaces empty result with a default key
 *
 * @param {string} key - The key to sanitize
 * @param {string} defaultKey - Default key if sanitization results in empty string
 * @returns {string} - Sanitized key
 */
export function sanitizeMongoKey(key, defaultKey = 'field') {
  if (!key || typeof key !== 'string') {
    return defaultKey;
  }

  // Remove dot characters
  let sanitized = key.replace(/\./g, '');

  // Remove $ characters from anywhere in the string
  sanitized = sanitized.replace(/\$/g, '');

  // Trim to max length
  if (sanitized.length > MAX_KEY_LENGTH) {
    sanitized = sanitized.substring(0, MAX_KEY_LENGTH);
  }

  // If empty after sanitization, use default
  if (sanitized.length === 0) {
    return defaultKey;
  }

  return sanitized;
}

/**
 * Sanitizes all keys in a layout structure
 * @param {Array} layoutRows - Array of layout rows with components
 * @returns {Object} - { sanitized: Array, changesMade: boolean, changes: Array }
 */
export function sanitizeLayoutKeys(layoutRows) {
  if (!Array.isArray(layoutRows)) {
    return { sanitized: layoutRows, changesMade: false, changes: [] };
  }

  const changes = [];
  let changesMade = false;

  const sanitized = layoutRows.map((row, rowIndex) => {
    const sanitizedRow = { ...row };

    // Sanitize row key
    if (row.key) {
      const newKey = sanitizeMongoKey(row.key, `row${rowIndex}`);
      if (newKey !== row.key) {
        changes.push({
          type: 'row',
          rowIndex,
          oldKey: row.key,
          newKey
        });
        sanitizedRow.key = newKey;
        changesMade = true;
      }
    }

    // Sanitize component keys
    if (Array.isArray(row.components)) {
      sanitizedRow.components = row.components.map((component, compIndex) => {
        const sanitizedComponent = { ...component };

        if (component.key) {
          const newKey = sanitizeMongoKey(component.key, `field${rowIndex}_${compIndex}`);
          if (newKey !== component.key) {
            changes.push({
              type: 'component',
              rowIndex,
              componentIndex: compIndex,
              oldKey: component.key,
              newKey
            });
            sanitizedComponent.key = newKey;
            changesMade = true;
          }
        }

        // Sanitize condition key if present
        if (component.condition && component.condition.key) {
          const newConditionKey = sanitizeMongoKey(component.condition.key);
          if (newConditionKey !== component.condition.key) {
            changes.push({
              type: 'condition',
              rowIndex,
              componentIndex: compIndex,
              oldKey: component.condition.key,
              newKey: newConditionKey
            });
            sanitizedComponent.condition = {
              ...component.condition,
              key: newConditionKey
            };
            changesMade = true;
          }
        }

        return sanitizedComponent;
      });
    }

    return sanitizedRow;
  });

  return { sanitized, changesMade, changes };
}

/**
 * Checks if a key is valid for MongoDB
 * @param {string} key - The key to check
 * @returns {boolean} - True if valid, false otherwise
 */
export function isValidMongoKey(key) {
  if (!key || typeof key !== 'string') {
    return false;
  }

  if (key.length === 0 || key.length > MAX_KEY_LENGTH) {
    return false;
  }

  if (key.includes('.') || key.includes('$')) {
    return false;
  }

  return true;
}
